import type { Category } from "../../../shared/types";
import { useCategories } from "../../../shared/stores/category-store";
import { getToday, addDays } from "../../../shared/types/date-helpers";
import { CalendarIcon, XMarkIcon, BellIcon, BellOffIcon } from "../../../ui/icons";
import { notificationsSupported } from "../../../shared/hooks/use-notifications";

interface QuickAddExpandedProps {
  readonly showCategoryPicker: boolean;
  readonly selectedCategory: Category;
  onCategoryChange: (category: Category) => void;
  readonly dueDate: string | null;
  onDueDateChange: (date: string | null) => void;
  readonly reminderMinutes: number | null;
  onReminderChange: (minutes: number | null) => void;
}

export function QuickAddExpanded({
  showCategoryPicker,
  selectedCategory,
  onCategoryChange,
  dueDate,
  onDueDateChange,
  reminderMinutes,
  onReminderChange,
}: QuickAddExpandedProps) {
  const { categories, labels, dotColors } = useCategories();

  const notifPermission = notificationsSupported && ("Notification" in window ? Notification.permission === "granted" : true);

  const REMINDER_OPTIONS = [
    { label: "Le jour", value: 0 },
    { label: "La veille", value: 1440 },
    { label: "2j avant", value: 2880 },
  ] as const;

  const DATE_OPTIONS = [
    { label: "Auj.", value: getToday() },
    { label: "Dem.", value: addDays(getToday(), 1) },
    { label: "+1 sem", value: addDays(getToday(), 7) },
  ] as const;

  return (
    <div className="flex flex-col gap-3 border-t border-border/60 px-3 py-3 bg-surface-alt/50">
      {showCategoryPicker && (
        <div>
          <span className="text-[10px] text-ink-faint uppercase tracking-wide mb-1.5 block">
            Catégorie
          </span>
          <div className="flex gap-1.5 flex-wrap">
            {categories.map((cat) => (
              <button
                key={cat}
                type="button"
                onClick={() => onCategoryChange(cat as Category)}
                className={`flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-medium rounded-md transition-colors ${
                  selectedCategory === cat
                    ? "bg-accent text-white"
                    : "text-ink-muted hover:text-ink hover:bg-surface"
                }`}
              >
                <div className={`w-1.5 h-1.5 rounded-full ${dotColors[cat] ?? "bg-gray-400"}`} />
                {labels[cat]}
              </button>
            ))}
          </div>
        </div>
      )}

      <div>
        <span className="text-[10px] text-ink-faint uppercase tracking-wide mb-1.5 block">
          Échéance
        </span>
        <div className="flex gap-1.5">
          {DATE_OPTIONS.map((opt) => (
            <button
              key={opt.label}
              type="button"
              onClick={() => onDueDateChange(opt.value)}
              className={`px-2.5 py-1 text-[11px] font-medium rounded-md transition-colors ${
                dueDate === opt.value
                  ? "bg-accent text-white"
                  : "text-ink-muted hover:text-ink hover:bg-surface"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {dueDate && (
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2 px-2.5 py-1.5 bg-accent/10 rounded-md text-xs">
            <span className="font-medium text-accent flex items-center gap-1">
              <CalendarIcon className="w-3 h-3" /> {dueDate}
            </span>
            <button
              type="button"
              onClick={() => onDueDateChange(null)}
              className="ml-auto text-accent/70 hover:text-accent"
            >
              <XMarkIcon className="w-3 h-3" />
            </button>
          </div>

          <div>
            <span className="text-[10px] text-ink-faint uppercase tracking-wide mb-1.5 block">
              <BellIcon className="w-3 h-3 inline mr-1" />
              Rappel
            </span>
            <div className="flex gap-1.5 flex-wrap">
              {REMINDER_OPTIONS.map((opt) => (
                <button
                  key={opt.label}
                  type="button"
                  onClick={() => onReminderChange(reminderMinutes === opt.value ? null : opt.value)}
                  className={`px-2.5 py-1 text-[11px] font-medium rounded-md transition-colors ${
                    reminderMinutes === opt.value
                      ? "bg-accent text-white"
                      : "text-ink-muted hover:text-ink hover:bg-surface"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            {reminderMinutes != null && !notifPermission && (
              <p className="flex items-center gap-1 text-[10px] text-amber-400">
                <BellOffIcon className="w-3 h-3" />
                Activez les notifications dans les paramètres
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
